<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('subjects.editTitle') }}
            </h6>
          </div>
        </div>
      </div>
      
      <LoadingSpinner v-if="isLoading" :message="$t('subjects.loadingData')" />
      
      <div v-else class="card-body px-0 pb-2">
        <SubjectForm
          ref="subjectForm"
          :initial-data="subject"
          :is-loading="isUpdating"
          :is-edit-mode="true"
          :submit-text="$t('subjects.saveChanges')"
          @submit="updateSubject"
          @cancel="navigateBack"
          @update:form-data="handleFormUpdate"
        />
        
        <div class="d-flex gap-3 mt-3 mx-2">
          <button
            type="button"
            class="btn btn-primary btn-sm flex-grow-1"
            @click="submitForm"
            :disabled="isUpdating || !hasChanges"
          >
            <span v-if="isUpdating">
              <i class="fas fa-spinner fa-spin ms-2"></i>
              {{ $t('subjects.saving') }}
            </span>
            <span v-else>
              <i class="fas fa-save ms-2"></i>
              {{ $t('subjects.saveChanges') }}
            </span>
          </button>
          
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm flex-grow-1"
            @click="navigateBack"
            :disabled="isUpdating"
          >
            <i class="fas fa-times ms-2"></i>
            {{ $t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useToast } from 'vue-toastification';
import SubjectForm from "@/components/features/subjects/SubjectForm.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import api from "@/api";
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const toast = useToast();

const isLoading = ref(false);
const isUpdating = ref(false);
const subject = ref(null);
const subjectForm = ref(null);
const originalSubjectData = ref(null);
const currentFormData = ref(null);

const hasChanges = computed(() => {
  if (!originalSubjectData.value || !currentFormData.value) return false;
  
  return Object.keys(currentFormData.value).some(key => {
    const currentValue = currentFormData.value[key];
    const originalValue = originalSubjectData.value[key];
    
    if (key === 'created_at' || key === 'updated_at') return false;
    if (currentValue === null || originalValue === null) return currentValue !== originalValue;
    return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
  });
});

const handleFormUpdate = (newData) => {
  currentFormData.value = { ...newData };
};

const fetchSubject = async () => {
  isLoading.value = true;
  try {
    const response = await api.get(`/subjects/${route.params.id}`);
    subject.value = response.data.data;
  } catch (error) {
    toast.error(t('subjects.fetchError'));
    console.error("Error fetching subject:", error);
    router.push({ name: 'subjects.index' });
  } finally {
    isLoading.value = false;
  }
};

const submitForm = () => {
  subjectForm.value?.submitForm();
};

const updateSubject = async (formData) => {
  if (!hasChanges.value) {
    toast.info(t('subjects.noChanges'));
    return;
  }

  isUpdating.value = true;
  try {
    await api.put(`/subjects/${route.params.id}`, formData);
    toast.success(t('subjects.updateSuccess'));
    router.push({ name: 'subjects.index' });
  } catch (error) {
    if (error.response?.status === 422) {
      toast.error(t('subjects.validationError'));
    } else {
      toast.error(error.response?.data?.message || t('subjects.updateFail'));
    }
    console.error("Error updating subject:", error);
  } finally {
    isUpdating.value = false;
  }
};

const navigateBack = () => {
  router.push({ name: 'subjects.index' });
};

watch(subject, (newSubjectData) => {
  if (newSubjectData && !originalSubjectData.value) {
    originalSubjectData.value = JSON.parse(JSON.stringify(newSubjectData));
    currentFormData.value = JSON.parse(JSON.stringify(newSubjectData));
  }
}, { immediate: true });

onMounted(() => fetchSubject());
</script>
